'use strict';
import noop from 'lodash/noop';
import {Observable} from 'rxjs/Observable';
import {getId} from '../../core-utilities/domain/ReduxUtils';
import {CLIENT_VERSION} from '../../config/constants';


UserPresenceService.$inject = [
  '$rootScope',
  'EventBus',
  'UserDomain',
  'fetchUtils',
  'DeviceServices',
];

export const INTERVAL = 5 * 60 * 1000; // notify the server that the user has been seen

/**
 * @enum
 */
export const PRESENCE_STATE = {
  'LEGACY': -1,
  'OFFLINE': 0,
  'AWAY': 1,
  'ONLINE': 2,
};

/**
 * Currently relies on the fact that the page is refreshed on logout
 * If the refresh is removed, We need to broadcast a logout event
 *
 * Concerns:
 * The current system of notifying for online vs away should work well for users only logged in with a single client.
 * Users logged into multiple clients will currently result in false negatives.
 */
export function UserPresenceService(
  $rootScope,
  EventBus,
  UserDomain,
  fetchUtils,
  DeviceServices,
) {
  // source: http://stackoverflow.com/a/19519701/1800748
  let supportsVisibilityApi = false;
  const vis = (function () {
    const keys = {
      hidden: 'visibilitychange',
      webkitHidden: 'webkitvisibilitychange',
      mozHidden: 'mozvisibilitychange',
      msHidden: 'msvisibilitychange',
    };
    let stateKey, eventKey;
    for (stateKey in keys) {
      if (stateKey in document) {
        supportsVisibilityApi = true;
        eventKey = keys[stateKey];
        break;
      }
    }
    return function (c) {
      if (c) document.addEventListener(eventKey, c);
      return !document[stateKey];
    };
  })();
  /**
   * @type {Object.<string, PresenceBinding>}
   */
  const personBindings = {};
  const personBindings2 = {};

  this.INTERVAL = INTERVAL;
  this.PRESENCE_STATE = PRESENCE_STATE;
  this.init = init;
  this.bindPresence = bindPresence;
  this.getPresence$ = getPresence$;
  this.getPresenceState = getPresenceState;

  function init() {
    if (supportsVisibilityApi) {
      EventBus.on('event:UserAvailable', watchPresence);
    }
  }

  function watchPresence() {
    let previousState = vis();
    notify();
    let intervalId = setInterval(notify, 0.95 * INTERVAL);

    vis(updateBinding);

    function updateBinding() {
      if (vis() && !previousState) {
        clearInterval(intervalId);
        notify();
        intervalId = setInterval(notify, 0.95 * INTERVAL);
      }
    }

    function notify() {
      previousState = vis();
      notifyPresence(previousState);
    }
  }

  function notifyPresence(isActive) {
    const request = {
      method: 'POST',
      mode: 'cors',
      cache: 'default',
      contentType: 'json',
      responseType: 'json',
      body: {
        isActive,
        device: DeviceServices.getBuildAwareDeviceType(),
        deviceVersion: DeviceServices.getDeviceVersion(),
        clientVersion: CLIENT_VERSION,
        ua: DeviceServices.getUserAgent(),
      },
    };

    const url = `${fetchUtils.API_URL}/api/${UserDomain.COLLECTION}/presence`;
    return fetchUtils.requestWithAuth(url, request)
      .catch(console.error.bind(console));
  }

  /**
   *
   * @param {$rootScope.Scope} $scope
   * @param {PersonDomain} person
   * @param {Function} cb
   * @returns {PresenceBinding}
   */
  function bindPresence($scope, person, cb) {
    cb = cb || noop;
    const personId = getId(person);
    let binding = personBindings[personId];
    if (binding) {
      if (binding.scopes.indexOf($scope) === -1) {
        binding.scopes.push($scope);
        binding._cb.push(cb);
        cb(binding.state, personId);
      }

      return binding;
    } else {
      binding = binding = {scopes: [$scope], _cb: [cb]};
    }

    const lastOnline$ = $rootScope
      .$toObservable(() => person.lastOnline)
      .map(returnNewValue);

    const lastSeen$ = $rootScope
      .$toObservable(() => person.lastSeen)
      .map(returnNewValue);

    // updateBinding();
    binding._s = Observable.merge(lastOnline$, lastSeen$).subscribe(updateBinding);

    $scope.$on('$destroy', cleanup);

    return binding;

    function updateBinding() {
      const lastOnline = getLastOnlineTime(person);
      const lastSeen = getLastSeenTime(person);
      const now = new Date().getTime() - INTERVAL;

      if (!lastOnline || !lastSeen) {
        binding.state = PRESENCE_STATE.LEGACY;
      } else if (now < lastOnline) {
        binding.state = PRESENCE_STATE.ONLINE;
      } else if (now < lastSeen) {
        binding.state = PRESENCE_STATE.AWAY;
      } else {
        binding.state = PRESENCE_STATE.OFFLINE;
      }

      binding._cb.forEach(cb => cb(binding.state, personId));
    }

    function cleanup() {
      const index = binding.scopes.indexOf($scope);
      if (index > -1) {
        if (binding.scopes.length === 1) {
          binding._s.unsubscribe();
          delete personBindings[personId];
        } else {
          binding.scopes.splice(index, 1);
          binding._cb.splice(index, 1);
        }
      }
    }
  }

  /**
   * @param {PersonDomain} person
   * @returns {PresenceBinding}
   */
  function getPresence$(person) {
    const personId = getId(person);
    let binding = personBindings2[personId];
    if (binding) {
      return binding;
    } else {
      binding = personBindings2[personId] = person.$observe()
        .startWith(getPresenceState(person))
        .distinctUntilChanged(null, () => `${getLastOnlineTime(person)}-${getLastSeenTime(person)}`)
        .map(() => getPresenceState(person))
        .publishReplay(1)
        .refCount();
      return binding;
    }
  }

  function getPresenceState(person) {
    const lastOnline = getLastOnlineTime(person);
    const lastSeen = getLastSeenTime(person);
    const now = new Date().getTime() - INTERVAL;

    if (!lastOnline || !lastSeen) {
      return PRESENCE_STATE.LEGACY;
    } else if (now < lastOnline) {
      return PRESENCE_STATE.ONLINE;
    } else if (now < lastSeen) {
      return PRESENCE_STATE.AWAY;
    } else {
      return PRESENCE_STATE.OFFLINE;
    }
  }

  function getLastOnlineTime(person) {
    return person.lastOnline ? person.lastOnline.getTime() : null;
  }

  function getLastSeenTime(person) {
    return person.lastSeen ? person.lastSeen.getTime() : null;
  }

  function returnNewValue(value) {
    return value && value.newValue;
  }

  /**
   * @typedef {object} PresenceBinding
   * @property {PRESENCE_STATE} state
   * @property {Array.<$rootScope.Scope>} scopes
   * @property {Array.<Function>} _cb
   * @property {Subscription} _s
   */
}



// WEBPACK FOOTER //
// ./src/js/auth/services/UserPresenceService.js